import { Component, inject, OnInit } from '@angular/core';
import { AuthService, Availability, AvailabilityService, CartRow, MetadataService, NavbarcartComponent, NavbarCartModel, PriceConfigService } from 'gung-standard';
import { first, forkJoin, mergeMap, of, switchMap } from 'rxjs';
import { ColmecCartService } from './../../services/colmec-cart.service';

@Component({
  selector: 'colmec-navbar-cart',
  templateUrl: './colmec-navbar-cart.component.html',
  styleUrls: ['./colmec-navbar-cart.component.scss'],
  standalone: false
})
export class ColmecNavbarCartComponent extends NavbarcartComponent implements OnInit {
  private colmecCartService = inject(ColmecCartService);
  private priceConfigService = inject(PriceConfigService);
  private metadataService = inject(MetadataService);
  private authService = inject(AuthService);
  private availabilityService = inject(AvailabilityService);

  productsAutoAdded = false;
  productsCount = 0;
  relatedConfigurationChecked = false;
  public hidePrice = false;

  public isDisabled: boolean;
  isUser = false;

  ngOnInit() {
    this.authService.getRoles().subscribe(roles => {
      const isView = roles.indexOf('VIEW') > -1;
      this.isDisabled = isView;
      this.isUser = roles.indexOf('USER') > -1 && roles.indexOf('ADMIN') === -1 && roles.indexOf('SALES') === -1;
    });
    const sub = this.navbarCartService
      .getContents()
      .pipe(
        mergeMap(cart =>
          forkJoin([
            of(cart),
            this.availabilityService.getAvailabilities(cart.rows.map(c => c.product.id)).pipe(first())
          ])
        ),
        switchMap(([cart, availabilities]) => {
          for (const cartRow of cart.rows) {
            const av = availabilities.find(a => a.productId === cartRow.product.id);
            cartRow.product.extra.availability = av;
          }
          return of(cart);
        })
      )
      .subscribe(d => {
        const cart: NavbarCartModel = JSON.parse(JSON.stringify(d));
        cart.rows = d.rows.filter(row => !row.cartRow.extra.autoAdded);
        this.productsAutoAdded = cart.rows.length !== d.rows.length;
        this.cartContents = cart;
        this.productsCount = cart.rows.map(row => Number(row.cartRow.qty)).reduce((acc, val) => acc + val, 0);

        if (!this.relatedConfigurationChecked) {
          // this makes sure that for products existing in the cart before the configuration has been add will be set
          this.confirmRelatedConfiguration(cart.rows.map(item => item.cartRow));
          this.relatedConfigurationChecked = true;
        }
      });

    this.subs.push(sub);

    this.priceConfigService.hidePrice.subscribe(hidePrice => (this.hidePrice = hidePrice));
  }

  // IMPORTANT_NOTE:
  // THIS IS A TRANSITION SOLUTION JUST TO MAKE SURE THAT THE AUTO_ADDED PRODUCTS ARE ADDED TO EXISTING PRODUCT CARTS IN LOCAL_STORAGE
  // AFTER THE FEATURE IS BEING LIVE FOR A WHILE, IT MAY BE REMOVED AS IT MAY NOT BE NEEDED ANYMORE
  confirmRelatedConfiguration(cartRows: CartRow[]): void {
    cartRows
      .filter(cartRow => cartRow.productId)
      .forEach(cartRow => {
        this.colmecCartService.setProductRelatedConfiguration(cartRow.productId);
      });
  }

  public removeRow(row: CartRow) {
    const index = this.cartContents.rows.findIndex(r => r.cartRow.productId === row.productId);
    if (index > -1) {
      this.cartContents.rows.splice(index, 1);
      this.colmecCartService.removeRow(row);
    }
  }

  public updateQty(row: CartRow, value: any) {
    const av: Availability = this.cartContents.rows.find(
      r => r.cartRow.productId === row.productId && r.cartRow.productPartialId === row.productPartialId
    ).product.extra.availability;
    if (this.isUser && value > av.currentAvailability) {
      value = av.currentAvailability;
    }
    // check qty
    if (row.qty === value) {
      return;
    }
    // value has changed, propagate throught the cart
    this.colmecCartService.setProductQuantity(row.productId, value, row.targetStockId);
  }

  getOptionName(table: string, value: string, key: string): string {
    const result = this.metadataService.getMetadataValue(table, value, key);
    if (result) {
      return result;
    }
    return '';
  }
}
